import { useLanguage } from "@/context/LanguageContext";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Globe, ChevronDown, Check } from "lucide-react";
import { toast } from "sonner";

/**
 * LanguageSelector Component
 * A reusable language selector dropdown that can be placed anywhere in the app
 * Language preference is persisted in localStorage and survives browser restarts
 * 
 * @param {string} variant - "light" (for dark backgrounds) or "dark" (for light backgrounds)
 * @param {boolean} showLabel - Whether to show the language name next to the flag
 * @param {string} className - Additional CSS classes
 */
const LanguageSelector = ({ 
  variant = "dark", 
  showLabel = true,
  className = "" 
}) => {
  const { language, setLanguage, availableLanguages } = useLanguage();
  
  const currentLang = availableLanguages.find(l => l.code === language);
  
  const buttonClasses = variant === "light" 
    ? "text-white hover:bg-white/10 border-white/20" 
    : "text-stone-700 hover:bg-stone-100 border-stone-200";
  
  const menuClasses = variant === "light"
    ? "bg-stone-900 border-stone-700"
    : "bg-white border-stone-200";
    
  const itemClasses = variant === "light"
    ? "text-white hover:bg-stone-800 focus:bg-stone-800"
    : "text-stone-800 hover:bg-stone-100 focus:bg-stone-100";
    
  const activeClasses = variant === "light"
    ? "bg-emerald-500/20 text-emerald-400"
    : "bg-emerald-50 text-emerald-600";

  const handleLanguageChange = (langCode) => {
    if (langCode !== language) {
      setLanguage(langCode);
      const selectedLang = availableLanguages.find(l => l.code === langCode);
      
      // Show toast with language-specific message
      const toastMessages = {
        en: "Language changed to English",
        es: "Idioma cambiado a Español",
        pt: "Idioma alterado para Português",
        ht: "Lang chanje pou Kreyòl"
      };
      
      toast.success(toastMessages[langCode] || `Language: ${selectedLang?.name}`, {
        icon: selectedLang?.flag,
        duration: 2000
      });
    }
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button 
          variant="outline" 
          size="sm"
          className={`gap-1.5 ${buttonClasses} ${className}`}
          data-testid="language-selector"
        >
          <Globe className="w-4 h-4" />
          <span className="text-lg">{currentLang?.flag}</span>
          {showLabel && <span className="hidden sm:inline">{currentLang?.name}</span>}
          <ChevronDown className="w-3 h-3 opacity-50" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className={menuClasses}>
        {availableLanguages.map((lang) => (
          <DropdownMenuItem
            key={lang.code}
            onClick={() => handleLanguageChange(lang.code)}
            className={`cursor-pointer ${itemClasses} ${language === lang.code ? activeClasses : ''}`}
            data-testid={`language-option-${lang.code}`}
          >
            <span className="mr-2 text-lg">{lang.flag}</span>
            <span className="flex-1">{lang.name}</span>
            {language === lang.code && <Check className="w-4 h-4 ml-2 text-emerald-500" />}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export default LanguageSelector;
